import type { z } from 'zod';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

/**
 * Authentication a route requires before its handler runs. Authorization on a specific resource
 * (e.g. "is this guest admitted to this screening") happens in the application layer.
 * - public: no session needed
 * - session: any signed-in subject (host account or guest)
 * - account: an active host account
 * - owner: an active account whose role can manage accounts
 */
export type RouteAuth = 'public' | 'session' | 'account' | 'owner';

export interface RouteDefinition {
  readonly method: HttpMethod;
  /** Express-style path; `:name` segments must match the keys of `params`. */
  readonly path: string;
  readonly auth: RouteAuth;
  readonly params?: z.ZodObject;
  readonly query?: z.ZodObject;
  readonly body?: z.ZodType;
  /** Text fields of a multipart body; the binary part is always named `file`. */
  readonly multipart?: z.ZodObject;
  /** Null for 204 responses. */
  readonly response: z.ZodType | null;
  readonly status: 200 | 201 | 204;
}

export const defineRoute = <const T extends RouteDefinition>(route: T): T => route;

type InputOf<T> = T extends z.ZodType ? z.input<T> : undefined;

export type RouteParams<R extends RouteDefinition> = InputOf<R['params']>;
export type RouteQuery<R extends RouteDefinition> = InputOf<R['query']>;
export type RouteBody<R extends RouteDefinition> = InputOf<R['body']>;
export type RouteMultipart<R extends RouteDefinition> = InputOf<R['multipart']>;
export type RouteResponse<R extends RouteDefinition> = R['response'] extends z.ZodType
  ? z.output<R['response']>
  : undefined;

/** Parsed (server-side) input types. */
export type ParsedParams<R extends RouteDefinition> = R['params'] extends z.ZodType
  ? z.output<R['params']>
  : undefined;
export type ParsedQuery<R extends RouteDefinition> = R['query'] extends z.ZodType
  ? z.output<R['query']>
  : undefined;
export type ParsedBody<R extends RouteDefinition> = R['body'] extends z.ZodType
  ? z.output<R['body']>
  : undefined;

const PARAM_PATTERN = /:([A-Za-z][A-Za-z0-9]*)/g;

export const pathParamNames = (path: string): string[] =>
  [...path.matchAll(PARAM_PATTERN)].map((m) => m[1] ?? '');

/** Fills `:name` segments, URL-encoding each value. */
export const buildPath = (path: string, params: Readonly<Record<string, string>> | undefined): string =>
  path.replace(PARAM_PATTERN, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(value);
  });
